import { RouteLink } from 'components/core/RouteLink';
import { Tabs } from 'components/core/Tabs';
import { useContext } from 'react';
import { Routes } from 'RoutePaths';
import { getWrappedAsset } from '../../utils/deso';
import { TradeActivityTableWrapper } from './ActivityTableWrapper';
import { APP_NAME_GENERIC, DESO_TOKEN_DISPLAY_NAME } from 'utils/constants';
import MyOrders from '../pages/MyOrders';
import { OpenfundContext } from '../../contexts/OpenfundContext';
import { ProfileEntryResponse } from 'deso-protocol';
import ProjectHoldersTable from './ProjectHoldersTable';
import { DESO_ZERO_PUBLIC_KEY } from '../../constants/AppConstants';
import { TWAPOrdersTable } from './TWAPOrdersTable';
import { useTradeActiveTab } from '../../hooks/useTradeActiveTab';

export function OpenOrdersTable(props: {
  loggedInPublicKey: string | undefined;
  project: ProfileEntryResponse;
  refreshOrderBookCallback: (skipOrderbookUpdate?: boolean) => void;
}) {
  const { currentUser, loadingUser } = useContext(OpenfundContext);

  const wrappedAsset = getWrappedAsset(props.project.Username);
  const isWrapped = !!wrappedAsset;
  const displayName = isWrapped ? wrappedAsset.displayName : props.project.Username;

  const { selectedTabIndex, setSelectedTabIndex } = useTradeActiveTab(0);

  const onSuccessfulCancel = () => {
    props.refreshOrderBookCallback();
  };

  const tabs = [
    {
      tab: `$${displayName}`,
      panel: (
        <TradeActivityTableWrapper
          tokenPublicKey={props.project.PublicKeyBase58Check}
          defaultFilter={{
            isMatchedOrder: {
              equalTo: false,
            },
          }}
        />
      ),
    },
    {
      tab: 'Holders',
      panel: (
        <ProjectHoldersTable
          publicKey={
            props.project.Username === DESO_TOKEN_DISPLAY_NAME
              ? DESO_ZERO_PUBLIC_KEY
              : props.project.PublicKeyBase58Check
          }
          className="border border-border rounded-lg"
        />
      ),
    },
    ...(currentUser
      ? [
          {
            tab: (
              <>
                <span className="sm:inline hidden">Open </span> Orders
              </>
            ),
            panel: (
              <MyOrders
                baseCurrencyPublicKey={props.project.PublicKeyBase58Check}
                heading={
                  <div className="font-sans text-xl text-muted-foreground font-semibold">
                    <div className="flex justify-between items-center lg:items-end">My Orders</div>
                  </div>
                }
                onCancel={onSuccessfulCancel}
              />
            ),
          },
          {
            tab: (
              <>
                TWAP<span className="sm:inline hidden"> Orders</span>
              </>
            ),
            panel: (
              <TWAPOrdersTable
                className="border border-border rounded-lg"
                baseCurrencyPublicKey={props.project.PublicKeyBase58Check}
              />
            ),
          },
          {
            tab: 'Trades',
            panel: (
              <TradeActivityTableWrapper
                tokenPublicKey={props.project.PublicKeyBase58Check}
                traderPublicKey={currentUser.PublicKeyBase58Check}
                title="My Trades"
                subtitle={`View your recent trades on the ${APP_NAME_GENERIC}`}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="w-full p-4 md:p-6">
      <header className="w-full flex items-center mb-4">
        <h2 className="text-lg font-semibold text-muted font-sans">Activity</h2>
        {props.loggedInPublicKey && (
          <div className="text-right ml-auto">
            <RouteLink kind="text-only" to={Routes.orders()}>
              <span className="hidden md:block text-muted hover:underline text-sm">View My Orders</span>
            </RouteLink>
          </div>
        )}
      </header>
      {!loadingUser && (
        <Tabs
          showSelect={false}
          onTabSelected={(_, index) => {
            setSelectedTabIndex(index);
          }}
          selectedIndex={selectedTabIndex}
          defaultIndex={selectedTabIndex}
          tabs={tabs}
        />
      )}
    </div>
  );
}
